import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { 
  LayoutDashboard, Users, Calendar, FileText, MessageSquare, Star, 
  BarChart3, LogOut, Menu, X, ChevronRight, Eye, Trash2, Edit, Plus,
  CheckCircle, Clock, XCircle, Save, ArrowLeft, Mail, Settings, Send, Bot, HelpCircle, Layers, Shield, Package, Share2, FileCode
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { adminApi } from '../lib/api';
import { toast } from 'sonner';
import CMSManager from '../components/admin/CMSManager';
import LegalAgreementsManager from '../components/admin/LegalAgreementsManager';
import PricingPackagesManager from '../components/admin/PricingPackagesManager';
import AvailabilityManager from '../components/admin/AvailabilityManager';
import SocialMediaManager from '../components/admin/SocialMediaManager';
import EmailTemplatesManager from '../components/admin/EmailTemplatesManager';

const statusColors = {
  pending: 'bg-yellow-100 text-yellow-800',
  confirmed: 'bg-green-100 text-green-800',
  completed: 'bg-blue-100 text-blue-800',
  cancelled: 'bg-red-100 text-red-800',
};

const blogCategories = [
  'Business Tips',
  'Trademark Insights',
  'Contract Law',
  'Government Contracting',
  'Legal Updates',
  'Case Studies',
];

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [adminName, setAdminName] = useState('');
  
  // Data states
  const [analytics, setAnalytics] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [leads, setLeads] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [posts, setPosts] = useState([]);

  // Editor states
  const [editingTestimonial, setEditingTestimonial] = useState(null);
  const [editingPost, setEditingPost] = useState(null);
  const [testimonialForm, setTestimonialForm] = useState({
    author_name: '',
    author_title: '',
    content: '',
    rating: 5,
  });
  const [postForm, setPostForm] = useState({
    title: '',
    slug: '',
    excerpt: '',
    content: '',
    category: '',
    author: 'Jennifer N. Brantley',
    is_published: false,
  });

  // Email settings state
  const [emailSettings, setEmailSettings] = useState({
    smtp_enabled: false,
    smtp_server: 'smtp.zoho.com',
    smtp_port: 587,
    sender_email: '',
    sender_password: '',
    sender_name: 'J. Brantley Law',
    notification_email: 'jbrantley@jenniferbrantleylaw.com',
    has_password: false,
  });
  const [testEmail, setTestEmail] = useState('');
  const [sendingTest, setSendingTest] = useState(false);
  const [savingEmail, setSavingEmail] = useState(false);

  // AI Assistant settings state
  const [aiSettings, setAiSettings] = useState({
    enabled: true,
    assistant_name: 'Legal Assistant',
    personality: 'professional',
    greeting_message: '',
    greeting_message_es: '',
    custom_instructions: '',
    custom_instructions_es: '',
    faqs: [],
    topics_to_avoid: [],
    always_recommend_consultation: true,
    show_disclaimer: true,
  });
  const [savingAI, setSavingAI] = useState(false);
  const [newFAQ, setNewFAQ] = useState({ question: '', answer: '', question_es: '', answer_es: '' });
  const [newTopicToAvoid, setNewTopicToAvoid] = useState('');

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin');
      return;
    }
    setAdminName(localStorage.getItem('admin_name') || 'Admin');
    loadData();
  }, [navigate]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [analyticsRes, bookingsRes, leadsRes, testimonialsRes, postsRes, emailRes, aiRes] = await Promise.all([
        adminApi.getAnalytics(),
        adminApi.getBookings(),
        adminApi.getLeads(),
        adminApi.getAllTestimonials(),
        adminApi.getAllPosts(),
        adminApi.getEmailSettings(),
        adminApi.getAISettings(),
      ]);
      setAnalytics(analyticsRes.data);
      setBookings(bookingsRes.data);
      setLeads(leadsRes.data);
      setTestimonials(testimonialsRes.data);
      setPosts(postsRes.data);
      setEmailSettings(emailRes.data);
      setAiSettings(aiRes.data);
    } catch (error) {
      if (error.response?.status === 401) {
        handleLogout();
      }
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_name');
    navigate('/admin');
  };

  const updateBookingStatus = async (id, status) => {
    try {
      await adminApi.updateBooking(id, status);
      toast.success('Booking updated');
      loadData();
    } catch (error) {
      toast.error('Failed to update booking');
    }
  };

  const deleteLead = async (id) => {
    if (!window.confirm('Are you sure you want to delete this lead?')) return;
    try {
      await adminApi.deleteLead(id);
      toast.success('Lead deleted');
      loadData();
    } catch (error) {
      toast.error('Failed to delete lead');
    }
  };

  // ==================== TESTIMONIAL FUNCTIONS ====================
  const resetTestimonialForm = () => {
    setTestimonialForm({ author_name: '', author_title: '', content: '', rating: 5 });
    setEditingTestimonial(null);
  };

  const startEditTestimonial = (testimonial) => {
    setTestimonialForm({
      author_name: testimonial.author_name,
      author_title: testimonial.author_title || '',
      content: testimonial.content,
      rating: testimonial.rating || 5,
    });
    setEditingTestimonial(testimonial.id);
  };

  const saveTestimonial = async () => {
    if (!testimonialForm.author_name || !testimonialForm.content) {
      toast.error('Please fill in name and content');
      return;
    }
    try {
      if (editingTestimonial && editingTestimonial !== 'new') {
        await adminApi.updateTestimonial(editingTestimonial, testimonialForm);
        toast.success('Testimonial updated');
      } else {
        await adminApi.createTestimonial(testimonialForm);
        toast.success('Testimonial created');
      }
      resetTestimonialForm();
      loadData();
    } catch (error) {
      toast.error('Failed to save testimonial');
    }
  };

  const deleteTestimonial = async (id) => {
    if (!window.confirm('Are you sure you want to delete this testimonial?')) return;
    try {
      await adminApi.deleteTestimonial(id);
      toast.success('Testimonial deleted');
      loadData();
    } catch (error) {
      toast.error('Failed to delete testimonial');
    }
  };

  // ==================== BLOG POST FUNCTIONS ====================
  const generateSlug = (title) => {
    return title
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');
  };

  const resetPostForm = () => {
    setPostForm({
      title: '',
      slug: '',
      excerpt: '',
      content: '',
      category: '',
      author: 'Jennifer N. Brantley',
      is_published: false,
    });
    setEditingPost(null);
  };

  const startEditPost = (post) => {
    setPostForm({
      title: post.title,
      slug: post.slug,
      excerpt: post.excerpt,
      content: post.content,
      category: post.category,
      author: post.author,
      is_published: post.is_published,
    });
    setEditingPost(post.id);
  };

  const savePost = async () => {
    if (!postForm.title || !postForm.content || !postForm.category || !postForm.excerpt) {
      toast.error('Please fill in all required fields');
      return;
    }
    const postData = {
      ...postForm,
      slug: postForm.slug || generateSlug(postForm.title),
    };
    try {
      if (editingPost && editingPost !== 'new') {
        await adminApi.updatePost(editingPost, postData);
        toast.success('Post updated');
      } else {
        await adminApi.createPost(postData);
        toast.success('Post created');
      }
      resetPostForm();
      loadData();
    } catch (error) {
      toast.error('Failed to save post');
    }
  };

  const deletePost = async (id) => {
    if (!window.confirm('Are you sure you want to delete this post?')) return;
    try {
      await adminApi.deletePost(id);
      toast.success('Post deleted');
      loadData();
    } catch (error) {
      toast.error('Failed to delete post');
    }
  };

  // ==================== EMAIL SETTINGS FUNCTIONS ====================
  const saveEmailSettings = async () => {
    setSavingEmail(true);
    try {
      await adminApi.updateEmailSettings(emailSettings);
      toast.success('Email settings saved successfully');
      loadData();
    } catch (error) {
      toast.error('Failed to save email settings');
    } finally {
      setSavingEmail(false);
    }
  };

  const sendTestEmailHandler = async () => {
    if (!testEmail) {
      toast.error('Please enter a test email address');
      return;
    }
    setSendingTest(true);
    try {
      await adminApi.sendTestEmail(testEmail);
      toast.success(`Test email sent to ${testEmail}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send test email');
    } finally {
      setSendingTest(false);
    }
  };

  // ==================== AI SETTINGS FUNCTIONS ====================
  const saveAISettings = async () => {
    setSavingAI(true);
    try {
      await adminApi.updateAISettings(aiSettings);
      toast.success('AI Assistant settings saved successfully');
      loadData();
    } catch (error) {
      toast.error('Failed to save AI settings');
    } finally {
      setSavingAI(false);
    }
  };

  const addFAQ = async () => {
    if (!newFAQ.question || !newFAQ.answer) {
      toast.error('Please fill in both question and answer');
      return;
    }
    try {
      await adminApi.addFAQ(newFAQ);
      toast.success('FAQ added successfully');
      setNewFAQ({ question: '', answer: '', question_es: '', answer_es: '' });
      loadData();
    } catch (error) {
      toast.error('Failed to add FAQ');
    }
  };

  const deleteFAQ = async (faqId) => {
    if (!window.confirm('Are you sure you want to delete this FAQ?')) return;
    try {
      await adminApi.deleteFAQ(faqId);
      toast.success('FAQ deleted');
      loadData();
    } catch (error) {
      toast.error('Failed to delete FAQ');
    }
  };

  const addTopicToAvoid = () => {
    if (!newTopicToAvoid.trim()) return;
    const topics = [...(aiSettings.topics_to_avoid || []), newTopicToAvoid.trim()];
    setAiSettings({ ...aiSettings, topics_to_avoid: topics });
    setNewTopicToAvoid('');
  };

  const removeTopicToAvoid = (index) => {
    const topics = aiSettings.topics_to_avoid.filter((_, i) => i !== index);
    setAiSettings({ ...aiSettings, topics_to_avoid: topics });
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const navItems = [
    { id: 'overview', label: 'Overview', icon: LayoutDashboard },
    { id: 'cms', label: 'CMS', icon: Layers },
    { id: 'legal', label: 'Legal Agreements', icon: Shield },
    { id: 'pricing', label: 'Pricing Packages', icon: Package },
    { id: 'availability', label: 'Scheduling', icon: Clock },
    { id: 'social', label: 'Social Media', icon: Share2 },
    { id: 'bookings', label: 'Bookings', icon: Calendar },
    { id: 'leads', label: 'Leads', icon: Users },
    { id: 'testimonials', label: 'Testimonials', icon: Star },
    { id: 'blog', label: 'Blog Posts', icon: FileText },
    { id: 'ai', label: 'AI Assistant', icon: Bot },
    { id: 'templates', label: 'Email Templates', icon: FileCode },
    { id: 'email', label: 'Email Settings', icon: Mail },
  ];

  return (
    <div className="min-h-screen bg-[#FDFCFA]" data-testid="admin-dashboard">
      {/* Mobile Header */}
      <div className="lg:hidden bg-white border-b border-[#E8E4DC] p-4 flex items-center justify-between">
        <h1 className="font-serif text-lg text-[#222222]">Admin Dashboard</h1>
        <button onClick={() => setSidebarOpen(!sidebarOpen)}>
          {sidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      <div className="flex">
        {/* Sidebar */}
        <aside className={`
          fixed lg:static inset-y-0 left-0 z-50 w-64 bg-[#222222] transform transition-transform duration-200
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        `}>
          <div className="p-6 border-b border-gray-800">
            <h1 className="text-xl font-serif text-white">J. Brantley Law</h1>
            <p className="text-xs text-[#C2A46D] font-mono uppercase tracking-wider mt-1">Admin Panel</p>
          </div>

          <nav className="p-4 space-y-1">
            {navItems.map((item) => (
              <button
                key={item.id}
                onClick={() => { setActiveTab(item.id); setSidebarOpen(false); resetTestimonialForm(); resetPostForm(); }}
                className={`w-full flex items-center gap-3 px-4 py-3 text-sm rounded transition-colors ${
                  activeTab === item.id
                    ? 'bg-[#1F2A44] text-white'
                    : 'text-gray-400 hover:bg-gray-800 hover:text-white'
                }`}
                data-testid={`nav-${item.id}`}
              >
                <item.icon className="w-5 h-5" />
                {item.label}
              </button>
            ))}
          </nav>

          <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-gray-800">
            <div className="flex items-center justify-between text-gray-400 text-sm mb-4">
              <span>{adminName}</span>
            </div>
            <Button
              onClick={handleLogout}
              variant="outline"
              className="w-full border-gray-700 text-gray-400 hover:bg-gray-800 hover:text-white"
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
            <Link to="/" className="block mt-3">
              <Button variant="ghost" className="w-full text-gray-500 hover:text-white text-xs">
                ← Back to Website
              </Button>
            </Link>
          </div>
        </aside>

        {/* Main Content */}
        <main className="flex-1 p-6 lg:p-8 min-h-screen">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1F2A44]"></div>
            </div>
          ) : (
            <>
              {/* Overview Tab */}
              {activeTab === 'overview' && (
                <div data-testid="overview-section">
                  <h2 className="text-2xl font-serif text-[#222222] mb-6">Dashboard Overview</h2>
                  
                  {/* Stats Grid */}
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                    <div className="bg-white border border-[#E8E4DC] p-6">
                      <div className="flex items-center justify-between mb-4">
                        <Calendar className="w-8 h-8 text-[#1F2A44]" />
                        <Badge className="bg-[#1F2A44] text-white">Total</Badge>
                      </div>
                      <p className="text-3xl font-serif text-[#222222]">{analytics?.total_bookings || 0}</p>
                      <p className="text-sm text-[#555555]">Total Bookings</p>
                    </div>
                    <div className="bg-white border border-[#E8E4DC] p-6">
                      <div className="flex items-center justify-between mb-4">
                        <Clock className="w-8 h-8 text-yellow-600" />
                        <Badge className="bg-yellow-100 text-yellow-800">Pending</Badge>
                      </div>
                      <p className="text-3xl font-serif text-[#222222]">{analytics?.pending_bookings || 0}</p>
                      <p className="text-sm text-[#555555]">Pending Review</p>
                    </div>
                    <div className="bg-white border border-[#E8E4DC] p-6">
                      <div className="flex items-center justify-between mb-4">
                        <Users className="w-8 h-8 text-blue-600" />
                        <Badge className="bg-blue-100 text-blue-800">Leads</Badge>
                      </div>
                      <p className="text-3xl font-serif text-[#222222]">{analytics?.total_leads || 0}</p>
                      <p className="text-sm text-[#555555]">Total Leads</p>
                    </div>
                    <div className="bg-white border border-[#E8E4DC] p-6">
                      <div className="flex items-center justify-between mb-4">
                        <BarChart3 className="w-8 h-8 text-green-600" />
                        <Badge className="bg-green-100 text-green-800">Views</Badge>
                      </div>
                      <p className="text-3xl font-serif text-[#222222]">{analytics?.page_views || 0}</p>
                      <p className="text-sm text-[#555555]">Page Views</p>
                    </div>
                  </div>

                  {/* Recent Bookings */}
                  <div className="bg-white border border-[#E8E4DC] p-6 mb-8">
                    <h3 className="text-lg font-serif text-[#222222] mb-4">Recent Bookings</h3>
                    <div className="space-y-3">
                      {bookings.slice(0, 5).map((booking) => (
                        <div key={booking.id} className="flex items-center justify-between py-2 border-b border-[#E8E4DC] last:border-0">
                          <div>
                            <p className="font-medium text-[#222222]">{booking.name}</p>
                            <p className="text-sm text-[#555555]">{booking.service_interest}</p>
                          </div>
                          <Badge className={statusColors[booking.status] || 'bg-gray-100 text-gray-800'}>
                            {booking.status}
                          </Badge>
                        </div>
                      ))}
                      {bookings.length === 0 && (
                        <p className="text-[#555555] text-center py-4">No bookings yet</p>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* CMS Tab */}
              {activeTab === 'cms' && (
                <CMSManager />
              )}

              {/* Legal Agreements Tab */}
              {activeTab === 'legal' && (
                <LegalAgreementsManager />
              )}

              {/* Pricing Packages Tab */}
              {activeTab === 'pricing' && (
                <PricingPackagesManager />
              )}

              {/* Availability/Scheduling Tab */}
              {activeTab === 'availability' && (
                <AvailabilityManager />
              )}

              {/* Social Media Tab */}
              {activeTab === 'social' && (
                <SocialMediaManager />
              )}

              {/* Bookings Tab */}
              {activeTab === 'bookings' && (
                <div data-testid="bookings-section">
                  <div className="flex items-center justify-between mb-6">
                    <div>
                      <h2 className="text-2xl font-serif text-[#222222]">Manage Bookings</h2>
                      <p className="text-sm text-[#555555]">View and manage all consultation requests</p>
                    </div>
                    <div className="flex gap-2">
                      <Badge className="bg-yellow-100 text-yellow-800">
                        {bookings.filter(b => b.status === 'pending').length} Pending
                      </Badge>
                      <Badge className="bg-green-100 text-green-800">
                        {bookings.filter(b => b.status === 'confirmed').length} Confirmed
                      </Badge>
                    </div>
                  </div>

                  {/* Bookings List */}
                  <div className="space-y-4">
                    {bookings.map((booking) => (
                      <div 
                        key={booking.id} 
                        className="bg-white border border-[#E8E4DC] overflow-hidden"
                        data-testid={`booking-${booking.id}`}
                      >
                        {/* Header with status badge */}
                        <div className="flex items-center justify-between p-4 bg-[#F5F3EF] border-b border-[#E8E4DC]">
                          <div className="flex items-center gap-4">
                            <div className="w-10 h-10 bg-[#1F2A44] rounded-full flex items-center justify-center text-white font-semibold">
                              {booking.name?.charAt(0)?.toUpperCase() || '?'}
                            </div>
                            <div>
                              <h3 className="font-semibold text-[#222222]">{booking.name}</h3>
                              <p className="text-sm text-[#555555]">
                                Submitted {formatDate(booking.created_at)}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-3">
                            <Badge className={`${statusColors[booking.status] || 'bg-gray-100 text-gray-800'} px-3 py-1`}>
                              {booking.status?.charAt(0)?.toUpperCase() + booking.status?.slice(1)}
                            </Badge>
                            {booking.urgency === 'urgent' && (
                              <Badge className="bg-red-100 text-red-800 px-3 py-1">
                                Urgent
                              </Badge>
                            )}
                          </div>
                        </div>

                        {/* Booking Details Grid */}
                        <div className="p-6">
                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {/* Appointment Info */}
                            <div className="space-y-4">
                              <h4 className="text-xs font-mono uppercase tracking-wider text-[#C2A46D] border-b border-[#E8E4DC] pb-2">
                                Appointment Details
                              </h4>
                              <div className="space-y-3">
                                <div className="flex items-center gap-3">
                                  <Calendar className="w-4 h-4 text-[#555555]" />
                                  <div>
                                    <p className="text-xs text-[#777777]">Preferred Date</p>
                                    <p className="font-medium text-[#222222]">
                                      {booking.preferred_date || 'Flexible'}
                                    </p>
                                  </div>
                                </div>
                                <div className="flex items-center gap-3">
                                  <Clock className="w-4 h-4 text-[#555555]" />
                                  <div>
                                    <p className="text-xs text-[#777777]">Preferred Time</p>
                                    <p className="font-medium text-[#222222]">
                                      {booking.preferred_time || 'Flexible'}
                                    </p>
                                  </div>
                                </div>
                                <div className="flex items-center gap-3">
                                  <FileText className="w-4 h-4 text-[#555555]" />
                                  <div>
                                    <p className="text-xs text-[#777777]">Service Requested</p>
                                    <p className="font-medium text-[#222222]">
                                      {booking.service_interest || 'Not specified'}
                                    </p>
                                  </div>
                                </div>
                              </div>
                            </div>

                            {/* Contact Info */}
                            <div className="space-y-4">
                              <h4 className="text-xs font-mono uppercase tracking-wider text-[#C2A46D] border-b border-[#E8E4DC] pb-2">
                                Contact Information
                              </h4>
                              <div className="space-y-3">
                                <div className="flex items-center gap-3">
                                  <Mail className="w-4 h-4 text-[#555555]" />
                                  <div>
                                    <p className="text-xs text-[#777777]">Email</p>
                                    <a href={`mailto:${booking.email}`} className="font-medium text-[#1F2A44] hover:underline">
                                      {booking.email}
                                    </a>
                                  </div>
                                </div>
                                <div className="flex items-center gap-3">
                                  <Users className="w-4 h-4 text-[#555555]" />
                                  <div>
                                    <p className="text-xs text-[#777777]">Phone</p>
                                    <a href={`tel:${booking.phone}`} className="font-medium text-[#1F2A44] hover:underline">
                                      {booking.phone || 'Not provided'}
                                    </a>
                                  </div>
                                </div>
                                <div className="flex items-center gap-3">
                                  <MessageSquare className="w-4 h-4 text-[#555555]" />
                                  <div>
                                    <p className="text-xs text-[#777777]">State</p>
                                    <p className="font-medium text-[#222222]">
                                      {booking.state || 'Not specified'}
                                    </p>
                                  </div>
                                </div>
                              </div>
                            </div>

                            {/* Business Info */}
                            <div className="space-y-4">
                              <h4 className="text-xs font-mono uppercase tracking-wider text-[#C2A46D] border-b border-[#E8E4DC] pb-2">
                                Business Information
                              </h4>
                              <div className="space-y-3">
                                <div>
                                  <p className="text-xs text-[#777777]">Business Name</p>
                                  <p className="font-medium text-[#222222]">
                                    {booking.business_name || 'Not provided'}
                                  </p>
                                </div>
                                <div>
                                  <p className="text-xs text-[#777777]">Urgency Level</p>
                                  <p className="font-medium text-[#222222] capitalize">
                                    {booking.urgency || 'Normal'}
                                  </p>
                                </div>
                                <div>
                                  <p className="text-xs text-[#777777]">Language Preference</p>
                                  <p className="font-medium text-[#222222]">
                                    {booking.language === 'es' ? 'Spanish' : 'English'}
                                  </p>
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Description / Comments */}
                          {booking.description && (
                            <div className="mt-6 pt-6 border-t border-[#E8E4DC]">
                              <h4 className="text-xs font-mono uppercase tracking-wider text-[#C2A46D] mb-3">
                                Client Comments / Description
                              </h4>
                              <div className="bg-[#F5F3EF] p-4 rounded">
                                <p className="text-[#555555] whitespace-pre-wrap">{booking.description}</p>
                              </div>
                            </div>
                          )}

                          {/* Actions */}
                          <div className="mt-6 pt-6 border-t border-[#E8E4DC] flex items-center justify-between">
                            <div className="text-sm text-[#777777]">
                              Booking ID: <span className="font-mono">{booking.id?.slice(0, 8)}</span>
                            </div>
                            <div className="flex gap-2">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => updateBookingStatus(booking.id, 'confirmed')}
                                className="text-green-600 border-green-600 hover:bg-green-50"
                                disabled={booking.status === 'confirmed'}
                              >
                                <CheckCircle className="w-4 h-4 mr-2" />
                                Confirm
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => updateBookingStatus(booking.id, 'cancelled')}
                                className="text-red-600 border-red-600 hover:bg-red-50"
                                disabled={booking.status === 'cancelled'}
                              >
                                <XCircle className="w-4 h-4 mr-2" />
                                Cancel
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => window.open(`mailto:${booking.email}?subject=Re: Your Consultation Request with J. Brantley Law`, '_blank')}
                                className="text-[#1F2A44] border-[#1F2A44] hover:bg-[#1F2A44] hover:text-white"
                              >
                                <Mail className="w-4 h-4 mr-2" />
                                Email Client
                              </Button>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {bookings.length === 0 && (
                    <div className="bg-white border border-[#E8E4DC] p-12 text-center">
                      <Calendar className="w-12 h-12 text-[#E8E4DC] mx-auto mb-4" />
                      <p className="text-[#555555]">No bookings yet</p>
                      <p className="text-sm text-[#777777] mt-2">When clients book consultations, they'll appear here.</p>
                    </div>
                  )}
                </div>
              )}

              {/* Leads Tab */}
              {activeTab === 'leads' && (
                <div data-testid="leads-section">
                  <h2 className="text-2xl font-serif text-[#222222] mb-6">Manage Leads</h2>
                  <div className="bg-white border border-[#E8E4DC] overflow-hidden">
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead className="bg-[#F5F3EF]">
                          <tr>
                            <th className="text-left p-4 text-sm font-medium text-[#555555]">Contact</th>
                            <th className="text-left p-4 text-sm font-medium text-[#555555]">Service</th>
                            <th className="text-left p-4 text-sm font-medium text-[#555555]">Message</th>
                            <th className="text-left p-4 text-sm font-medium text-[#555555]">Date</th>
                            <th className="text-left p-4 text-sm font-medium text-[#555555]">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {leads.map((lead) => (
                            <tr key={lead.id} className="border-t border-[#E8E4DC]">
                              <td className="p-4">
                                <p className="font-medium text-[#222222]">{lead.name}</p>
                                <p className="text-sm text-[#555555]">{lead.email}</p>
                              </td>
                              <td className="p-4 text-sm text-[#555555]">{lead.service_interest || '-'}</td>
                              <td className="p-4 text-sm text-[#555555] max-w-xs truncate">{lead.message || '-'}</td>
                              <td className="p-4 text-sm text-[#555555]">{formatDate(lead.created_at)}</td>
                              <td className="p-4">
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => deleteLead(lead.id)}
                                  className="text-red-600 border-red-600 hover:bg-red-50"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    {leads.length === 0 && (
                      <p className="text-center py-8 text-[#555555]">No leads yet</p>
                    )}
                  </div>
                </div>
              )}

              {/* Testimonials Tab */}
              {activeTab === 'testimonials' && (
                <div data-testid="testimonials-section">
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="text-2xl font-serif text-[#222222]">Manage Testimonials</h2>
                    {!editingTestimonial && testimonialForm.author_name === '' && (
                      <Button
                        onClick={() => setEditingTestimonial('new')}
                        className="bg-[#1F2A44] hover:bg-[#151D30] text-white"
                        data-testid="add-testimonial-btn"
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        Add Testimonial
                      </Button>
                    )}
                  </div>

                  {/* Testimonial Form */}
                  {(editingTestimonial || testimonialForm.author_name !== '') && (
                    <div className="bg-white border border-[#E8E4DC] p-6 mb-6" data-testid="testimonial-form">
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="text-lg font-serif text-[#222222]">
                          {editingTestimonial === 'new' ? 'Add New Testimonial' : 'Edit Testimonial'}
                        </h3>
                        <Button variant="ghost" size="sm" onClick={resetTestimonialForm}>
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                        <div>
                          <Label>Client Name *</Label>
                          <Input
                            value={testimonialForm.author_name}
                            onChange={(e) => setTestimonialForm({...testimonialForm, author_name: e.target.value})}
                            className="mt-1"
                            placeholder="John Smith"
                            data-testid="testimonial-name"
                          />
                        </div>
                        <div>
                          <Label>Title/Company</Label>
                          <Input
                            value={testimonialForm.author_title}
                            onChange={(e) => setTestimonialForm({...testimonialForm, author_title: e.target.value})}
                            className="mt-1"
                            placeholder="CEO, Acme Inc."
                            data-testid="testimonial-title"
                          />
                        </div>
                      </div>
                      <div className="mb-4">
                        <Label>Testimonial Content *</Label>
                        <Textarea
                          value={testimonialForm.content}
                          onChange={(e) => setTestimonialForm({...testimonialForm, content: e.target.value})}
                          className="mt-1"
                          rows={4}
                          placeholder="Share what the client said about your services..."
                          data-testid="testimonial-content"
                        />
                      </div>
                      <div className="mb-4">
                        <Label>Rating</Label>
                        <Select
                          value={String(testimonialForm.rating)}
                          onValueChange={(val) => setTestimonialForm({...testimonialForm, rating: parseInt(val)})}
                        >
                          <SelectTrigger className="mt-1 w-32">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {[5, 4, 3, 2, 1].map(r => (
                              <SelectItem key={r} value={String(r)}>{r} Stars</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="flex gap-2">
                        <Button onClick={saveTestimonial} className="bg-[#1F2A44] hover:bg-[#151D30] text-white" data-testid="save-testimonial">
                          <Save className="w-4 h-4 mr-2" />
                          Save Testimonial
                        </Button>
                        <Button variant="outline" onClick={resetTestimonialForm}>Cancel</Button>
                      </div>
                    </div>
                  )}

                  {/* Testimonials List */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {testimonials.map((testimonial) => (
                      <div key={testimonial.id} className="bg-white border border-[#E8E4DC] p-6">
                        <div className="flex items-start justify-between mb-4">
                          <div>
                            <p className="font-medium text-[#222222]">{testimonial.author_name}</p>
                            <p className="text-sm text-[#555555]">{testimonial.author_title}</p>
                          </div>
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => startEditTestimonial(testimonial)}
                              className="text-[#1F2A44] border-[#1F2A44] hover:bg-[#1F2A44] hover:text-white"
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => deleteTestimonial(testimonial.id)}
                              className="text-red-600 border-red-600 hover:bg-red-50"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                        <p className="text-[#555555] text-sm italic">"{testimonial.content}"</p>
                        <div className="flex items-center gap-1 mt-4">
                          {[...Array(testimonial.rating || 5)].map((_, i) => (
                            <Star key={i} className="w-4 h-4 fill-[#C2A46D] text-[#C2A46D]" />
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                  {testimonials.length === 0 && !editingTestimonial && (
                    <div className="text-center py-12 bg-white border border-[#E8E4DC]">
                      <Star className="w-12 h-12 text-[#E8E4DC] mx-auto mb-4" />
                      <p className="text-[#555555] mb-4">No testimonials yet</p>
                      <Button
                        onClick={() => setEditingTestimonial('new')}
                        className="bg-[#1F2A44] hover:bg-[#151D30] text-white"
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        Add Your First Testimonial
                      </Button>
                    </div>
                  )}
                </div>
              )}

              {/* Blog Tab */}
              {activeTab === 'blog' && (
                <div data-testid="blog-section">
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="text-2xl font-serif text-[#222222]">Manage Blog Posts</h2>
                    {!editingPost && postForm.title === '' && (
                      <Button
                        onClick={() => setEditingPost('new')}
                        className="bg-[#1F2A44] hover:bg-[#151D30] text-white"
                        data-testid="add-post-btn"
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        New Post
                      </Button>
                    )}
                  </div>

                  {/* Blog Post Form */}
                  {(editingPost || postForm.title !== '') && (
                    <div className="bg-white border border-[#E8E4DC] p-6 mb-6" data-testid="post-form">
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="text-lg font-serif text-[#222222]">
                          {editingPost === 'new' ? 'Create New Post' : 'Edit Post'}
                        </h3>
                        <Button variant="ghost" size="sm" onClick={resetPostForm}>
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                        <div>
                          <Label>Title *</Label>
                          <Input
                            value={postForm.title}
                            onChange={(e) => setPostForm({...postForm, title: e.target.value, slug: generateSlug(e.target.value)})}
                            className="mt-1"
                            placeholder="5 Things to Know Before Registering a Trademark"
                            data-testid="post-title"
                          />
                        </div>
                        <div>
                          <Label>URL Slug</Label>
                          <Input
                            value={postForm.slug}
                            onChange={(e) => setPostForm({...postForm, slug: e.target.value})}
                            className="mt-1"
                            placeholder="5-things-before-registering-trademark"
                            data-testid="post-slug"
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                        <div>
                          <Label>Category *</Label>
                          <Select
                            value={postForm.category}
                            onValueChange={(val) => setPostForm({...postForm, category: val})}
                          >
                            <SelectTrigger className="mt-1" data-testid="post-category">
                              <SelectValue placeholder="Select category" />
                            </SelectTrigger>
                            <SelectContent>
                              {blogCategories.map(cat => (
                                <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label>Author</Label>
                          <Input
                            value={postForm.author}
                            onChange={(e) => setPostForm({...postForm, author: e.target.value})}
                            className="mt-1"
                            data-testid="post-author"
                          />
                        </div>
                      </div>
                      <div className="mb-4">
                        <Label>Excerpt *</Label>
                        <Textarea
                          value={postForm.excerpt}
                          onChange={(e) => setPostForm({...postForm, excerpt: e.target.value})}
                          className="mt-1"
                          rows={2}
                          placeholder="A brief summary that appears in blog listings..."
                          data-testid="post-excerpt"
                        />
                      </div>
                      <div className="mb-4">
                        <Label>Content *</Label>
                        <Textarea
                          value={postForm.content}
                          onChange={(e) => setPostForm({...postForm, content: e.target.value})}
                          className="mt-1"
                          rows={10}
                          placeholder="Write your blog post content here... (Supports basic markdown)"
                          data-testid="post-content"
                        />
                      </div>
                      <div className="flex items-center gap-4 mb-4">
                        <div className="flex items-center gap-2">
                          <Switch
                            checked={postForm.is_published}
                            onCheckedChange={(checked) => setPostForm({...postForm, is_published: checked})}
                            data-testid="post-published"
                          />
                          <Label>Publish immediately</Label>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button onClick={savePost} className="bg-[#1F2A44] hover:bg-[#151D30] text-white" data-testid="save-post">
                          <Save className="w-4 h-4 mr-2" />
                          {postForm.is_published ? 'Publish Post' : 'Save Draft'}
                        </Button>
                        <Button variant="outline" onClick={resetPostForm}>Cancel</Button>
                      </div>
                    </div>
                  )}

                  {/* Blog Posts List */}
                  <div className="bg-white border border-[#E8E4DC] overflow-hidden">
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead className="bg-[#F5F3EF]">
                          <tr>
                            <th className="text-left p-4 text-sm font-medium text-[#555555]">Title</th>
                            <th className="text-left p-4 text-sm font-medium text-[#555555]">Category</th>
                            <th className="text-left p-4 text-sm font-medium text-[#555555]">Status</th>
                            <th className="text-left p-4 text-sm font-medium text-[#555555]">Date</th>
                            <th className="text-left p-4 text-sm font-medium text-[#555555]">Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {posts.map((post) => (
                            <tr key={post.id} className="border-t border-[#E8E4DC]">
                              <td className="p-4">
                                <p className="font-medium text-[#222222]">{post.title}</p>
                                <p className="text-xs text-[#555555]">/{post.slug}</p>
                              </td>
                              <td className="p-4 text-sm text-[#555555]">{post.category}</td>
                              <td className="p-4">
                                <Badge className={post.is_published ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}>
                                  {post.is_published ? 'Published' : 'Draft'}
                                </Badge>
                              </td>
                              <td className="p-4 text-sm text-[#555555]">{formatDate(post.created_at)}</td>
                              <td className="p-4">
                                <div className="flex gap-2">
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => startEditPost(post)}
                                    className="text-[#1F2A44] border-[#1F2A44] hover:bg-[#1F2A44] hover:text-white"
                                  >
                                    <Edit className="w-4 h-4" />
                                  </Button>
                                  <Link to={`/resources/${post.slug}`} target="_blank">
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      className="text-blue-600 border-blue-600 hover:bg-blue-50"
                                    >
                                      <Eye className="w-4 h-4" />
                                    </Button>
                                  </Link>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => deletePost(post.id)}
                                    className="text-red-600 border-red-600 hover:bg-red-50"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    {posts.length === 0 && !editingPost && (
                      <div className="text-center py-12">
                        <FileText className="w-12 h-12 text-[#E8E4DC] mx-auto mb-4" />
                        <p className="text-[#555555] mb-4">No blog posts yet</p>
                        <Button
                          onClick={() => setEditingPost('new')}
                          className="bg-[#1F2A44] hover:bg-[#151D30] text-white"
                        >
                          <Plus className="w-4 h-4 mr-2" />
                          Create Your First Post
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* AI Assistant Settings Tab */}
              {activeTab === 'ai' && (
                <div className="space-y-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <h2 className="text-2xl font-serif text-[#222222]">AI Assistant Settings</h2>
                      <p className="text-sm text-[#555555]">Customize how the AI legal assistant behaves and responds</p>
                    </div>
                  </div>

                  {/* Enable/Disable & Basic Settings */}
                  <div className="bg-white border border-[#E8E4DC] p-6 space-y-6">
                    <div className="flex items-center justify-between p-4 bg-[#F5F3EF] border border-[#E8E4DC]">
                      <div>
                        <h3 className="font-medium text-[#222222]">Enable AI Assistant</h3>
                        <p className="text-sm text-[#555555]">When enabled, visitors can chat with the AI assistant</p>
                      </div>
                      <Switch
                        checked={aiSettings.enabled}
                        onCheckedChange={(checked) => setAiSettings({...aiSettings, enabled: checked})}
                        data-testid="ai-enabled-toggle"
                      />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="assistant_name">Assistant Name</Label>
                        <Input
                          id="assistant_name"
                          value={aiSettings.assistant_name || ''}
                          onChange={(e) => setAiSettings({...aiSettings, assistant_name: e.target.value})}
                          placeholder="Legal Assistant"
                          className="border-[#E8E4DC]"
                          data-testid="ai-name-input"
                        />
                      </div>

                      <div>
                        <Label htmlFor="personality">Personality / Tone</Label>
                        <Select
                          value={aiSettings.personality}
                          onValueChange={(value) => setAiSettings({...aiSettings, personality: value})}
                        >
                          <SelectTrigger data-testid="ai-personality-select">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="professional">Professional</SelectItem>
                            <SelectItem value="friendly">Friendly & Approachable</SelectItem>
                            <SelectItem value="formal">Formal & Authoritative</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <div className="flex items-center justify-between p-4 bg-[#F5F3EF] border border-[#E8E4DC]">
                      <div>
                        <h3 className="font-medium text-[#222222]">Always Recommend Consultation</h3>
                        <p className="text-sm text-[#555555]">AI will suggest booking a consultation in responses</p>
                      </div>
                      <Switch
                        checked={aiSettings.always_recommend_consultation}
                        onCheckedChange={(checked) => setAiSettings({...aiSettings, always_recommend_consultation: checked})}
                      />
                    </div>

                    <div className="flex items-center justify-between p-4 bg-[#F5F3EF] border border-[#E8E4DC]">
                      <div>
                        <h3 className="font-medium text-[#222222]">Show Legal Disclaimer</h3>
                        <p className="text-sm text-[#555555]">AI will include attorney-client disclaimer when appropriate</p>
                      </div>
                      <Switch
                        checked={aiSettings.show_disclaimer}
                        onCheckedChange={(checked) => setAiSettings({...aiSettings, show_disclaimer: checked})}
                      />
                    </div>
                  </div>

                  {/* Custom Instructions */}
                  <div className="bg-white border border-[#E8E4DC] p-6 space-y-4">
                    <h3 className="font-semibold text-[#222222] flex items-center gap-2">
                      <Settings className="w-4 h-4" />
                      Custom Instructions
                    </h3>
                    <p className="text-sm text-[#555555]">
                      Add specific instructions for how the AI should respond. These will be added to the AI's knowledge.
                    </p>
                    
                    <div>
                      <Label htmlFor="custom_instructions">English Instructions</Label>
                      <Textarea
                        id="custom_instructions"
                        value={aiSettings.custom_instructions || ''}
                        onChange={(e) => setAiSettings({...aiSettings, custom_instructions: e.target.value})}
                        placeholder="Example: Always mention our free initial consultation. Emphasize our experience with military families. Never discuss competitor firms..."
                        className="border-[#E8E4DC] min-h-[100px]"
                        data-testid="ai-instructions-en"
                      />
                    </div>

                    <div>
                      <Label htmlFor="custom_instructions_es">Spanish Instructions</Label>
                      <Textarea
                        id="custom_instructions_es"
                        value={aiSettings.custom_instructions_es || ''}
                        onChange={(e) => setAiSettings({...aiSettings, custom_instructions_es: e.target.value})}
                        placeholder="Ejemplo: Siempre mencionar nuestra consulta inicial gratuita..."
                        className="border-[#E8E4DC] min-h-[100px]"
                        data-testid="ai-instructions-es"
                      />
                    </div>
                  </div>

                  {/* FAQs */}
                  <div className="bg-white border border-[#E8E4DC] p-6 space-y-4">
                    <h3 className="font-semibold text-[#222222] flex items-center gap-2">
                      <HelpCircle className="w-4 h-4" />
                      Custom FAQs
                    </h3>
                    <p className="text-sm text-[#555555]">
                      Add frequently asked questions. The AI will use these to provide consistent, accurate answers.
                    </p>

                    {/* Existing FAQs */}
                    {aiSettings.faqs && aiSettings.faqs.length > 0 && (
                      <div className="space-y-3">
                        {aiSettings.faqs.map((faq, index) => (
                          <div key={faq.id || index} className="p-4 bg-[#F5F3EF] border border-[#E8E4DC]">
                            <div className="flex justify-between items-start mb-2">
                              <div className="flex-1">
                                <p className="font-medium text-[#222222]">Q: {faq.question}</p>
                                <p className="text-sm text-[#555555] mt-1">A: {faq.answer}</p>
                                {faq.question_es && (
                                  <p className="text-xs text-[#777777] mt-2">ES: {faq.question_es}</p>
                                )}
                              </div>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => deleteFAQ(faq.id)}
                                className="text-red-500 hover:text-red-700 hover:bg-red-50"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Add New FAQ */}
                    <div className="border border-dashed border-[#E8E4DC] p-4 space-y-3">
                      <p className="text-sm font-medium text-[#555555]">Add New FAQ</p>
                      <div className="grid grid-cols-1 gap-3">
                        <Input
                          value={newFAQ.question}
                          onChange={(e) => setNewFAQ({...newFAQ, question: e.target.value})}
                          placeholder="Question (English)"
                          className="border-[#E8E4DC]"
                        />
                        <Textarea
                          value={newFAQ.answer}
                          onChange={(e) => setNewFAQ({...newFAQ, answer: e.target.value})}
                          placeholder="Answer (English)"
                          className="border-[#E8E4DC] min-h-[60px]"
                        />
                        <Input
                          value={newFAQ.question_es}
                          onChange={(e) => setNewFAQ({...newFAQ, question_es: e.target.value})}
                          placeholder="Question (Spanish - optional)"
                          className="border-[#E8E4DC]"
                        />
                        <Textarea
                          value={newFAQ.answer_es}
                          onChange={(e) => setNewFAQ({...newFAQ, answer_es: e.target.value})}
                          placeholder="Answer (Spanish - optional)"
                          className="border-[#E8E4DC] min-h-[60px]"
                        />
                      </div>
                      <Button
                        onClick={addFAQ}
                        variant="outline"
                        className="border-[#C2A46D] text-[#C2A46D] hover:bg-[#C2A46D] hover:text-white"
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        Add FAQ
                      </Button>
                    </div>
                  </div>

                  {/* Topics to Avoid */}
                  <div className="bg-white border border-[#E8E4DC] p-6 space-y-4">
                    <h3 className="font-semibold text-[#222222] flex items-center gap-2">
                      <XCircle className="w-4 h-4" />
                      Topics to Avoid
                    </h3>
                    <p className="text-sm text-[#555555]">
                      List topics the AI should not discuss or provide information about.
                    </p>

                    {/* Existing Topics */}
                    {aiSettings.topics_to_avoid && aiSettings.topics_to_avoid.length > 0 && (
                      <div className="flex flex-wrap gap-2">
                        {aiSettings.topics_to_avoid.map((topic, index) => (
                          <Badge key={index} variant="secondary" className="bg-red-100 text-red-800 px-3 py-1">
                            {topic}
                            <button
                              onClick={() => removeTopicToAvoid(index)}
                              className="ml-2 hover:text-red-600"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </Badge>
                        ))}
                      </div>
                    )}

                    {/* Add New Topic */}
                    <div className="flex gap-2">
                      <Input
                        value={newTopicToAvoid}
                        onChange={(e) => setNewTopicToAvoid(e.target.value)}
                        placeholder="e.g., Criminal law, Immigration..."
                        className="border-[#E8E4DC] max-w-sm"
                        onKeyPress={(e) => e.key === 'Enter' && addTopicToAvoid()}
                      />
                      <Button
                        onClick={addTopicToAvoid}
                        variant="outline"
                        className="border-[#E8E4DC]"
                      >
                        <Plus className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>

                  {/* Save Button */}
                  <div className="pt-4">
                    <Button
                      onClick={saveAISettings}
                      disabled={savingAI}
                      className="bg-[#1F2A44] hover:bg-[#151D30] text-white"
                      data-testid="save-ai-settings"
                    >
                      <Save className="w-4 h-4 mr-2" />
                      {savingAI ? 'Saving...' : 'Save AI Settings'}
                    </Button>
                  </div>
                </div>
              )}

              {/* Email Templates Tab */}
              {activeTab === 'templates' && (
                <EmailTemplatesManager />
              )}

              {/* Email Settings Tab */}
              {activeTab === 'email' && (
                <div className="space-y-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <h2 className="text-2xl font-serif text-[#222222]">Email Settings</h2>
                      <p className="text-sm text-[#555555]">Configure Zoho SMTP for booking confirmation emails</p>
                    </div>
                  </div>

                  {/* Zoho Setup Guide */}
                  <div className="bg-blue-50 border border-blue-200 p-4 rounded">
                    <h3 className="font-semibold text-blue-900 mb-2">📧 Zoho SMTP Setup Guide</h3>
                    <ol className="text-sm text-blue-800 space-y-2 list-decimal list-inside">
                      <li>Log into your Zoho Mail account at <a href="https://mail.zoho.com" target="_blank" rel="noopener noreferrer" className="underline">mail.zoho.com</a></li>
                      <li>Go to <strong>Settings → Security → App Passwords</strong></li>
                      <li>Click <strong>"Generate New Password"</strong> and name it "Website Email"</li>
                      <li>Copy the generated password and paste it below</li>
                      <li>Use <code className="bg-blue-100 px-1 rounded">smtppro.zoho.com</code> for paid accounts, <code className="bg-blue-100 px-1 rounded">smtp.zoho.com</code> for free</li>
                    </ol>
                    <p className="text-xs text-blue-700 mt-3">
                      <strong>Important:</strong> If you have 2-Factor Authentication enabled, you MUST use an App Password, not your regular Zoho password.
                    </p>
                  </div>

                  <div className="bg-white border border-[#E8E4DC] p-6 space-y-6">
                    {/* Enable/Disable Toggle */}
                    <div className="flex items-center justify-between p-4 bg-[#F5F3EF] border border-[#E8E4DC]">
                      <div>
                        <h3 className="font-medium text-[#222222]">Enable Email Notifications</h3>
                        <p className="text-sm text-[#555555]">When enabled, booking confirmations will be sent automatically</p>
                      </div>
                      <Switch
                        checked={emailSettings.smtp_enabled}
                        onCheckedChange={(checked) => setEmailSettings({...emailSettings, smtp_enabled: checked})}
                        data-testid="email-enabled-toggle"
                      />
                    </div>

                    {/* Status Indicator */}
                    <div className={`p-4 flex items-center gap-3 ${emailSettings.smtp_enabled && emailSettings.has_password ? 'bg-green-50 border-green-200' : 'bg-yellow-50 border-yellow-200'} border`}>
                      {emailSettings.smtp_enabled && emailSettings.has_password ? (
                        <>
                          <CheckCircle className="w-5 h-5 text-green-600" />
                          <span className="text-green-800">Email service is configured and active</span>
                        </>
                      ) : (
                        <>
                          <Clock className="w-5 h-5 text-yellow-600" />
                          <span className="text-yellow-800">Email service requires configuration</span>
                        </>
                      )}
                    </div>

                    {/* SMTP Settings */}
                    <div className="space-y-4">
                      <h3 className="font-semibold text-[#222222] flex items-center gap-2">
                        <Settings className="w-4 h-4" />
                        Zoho SMTP Configuration
                      </h3>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="smtp_server">SMTP Server</Label>
                          <Select
                            value={emailSettings.smtp_server}
                            onValueChange={(value) => setEmailSettings({...emailSettings, smtp_server: value})}
                          >
                            <SelectTrigger data-testid="smtp-server-select">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="smtp.zoho.com">smtp.zoho.com (Personal/Free)</SelectItem>
                              <SelectItem value="smtppro.zoho.com">smtppro.zoho.com (Paid/Organization)</SelectItem>
                            </SelectContent>
                          </Select>
                          <p className="text-xs text-[#777777] mt-1">Choose based on your Zoho account type</p>
                        </div>

                        <div>
                          <Label htmlFor="smtp_port">SMTP Port</Label>
                          <Select
                            value={String(emailSettings.smtp_port)}
                            onValueChange={(value) => setEmailSettings({...emailSettings, smtp_port: parseInt(value)})}
                          >
                            <SelectTrigger data-testid="smtp-port-select">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="587">587 (TLS - Recommended)</SelectItem>
                              <SelectItem value="465">465 (SSL)</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="sender_email">Zoho Email Address</Label>
                          <Input
                            id="sender_email"
                            type="email"
                            value={emailSettings.sender_email || ''}
                            onChange={(e) => setEmailSettings({...emailSettings, sender_email: e.target.value})}
                            placeholder="jbrantley@jenniferbrantleylaw.com"
                            className="border-[#E8E4DC]"
                            data-testid="sender-email-input"
                          />
                        </div>

                        <div>
                          <Label htmlFor="sender_password">
                            App Password (NOT your regular Zoho password)
                            {emailSettings.has_password && <span className="text-green-600 ml-2">(Saved)</span>}
                          </Label>
                          <Input
                            id="sender_password"
                            type="password"
                            value={emailSettings.sender_password || ''}
                            onChange={(e) => setEmailSettings({...emailSettings, sender_password: e.target.value})}
                            placeholder={emailSettings.has_password ? "••••••••••••" : "Paste your Zoho App Password here"}
                            className="border-[#E8E4DC]"
                            data-testid="sender-password-input"
                          />
                          <p className="text-xs text-amber-600 mt-1 font-medium">
                            ⚠️ Generate an App Password in Zoho: Settings → Security → App Passwords
                          </p>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="sender_name">Sender Display Name</Label>
                          <Input
                            id="sender_name"
                            value={emailSettings.sender_name || ''}
                            onChange={(e) => setEmailSettings({...emailSettings, sender_name: e.target.value})}
                            placeholder="J. Brantley Law"
                            className="border-[#E8E4DC]"
                            data-testid="sender-name-input"
                          />
                        </div>

                        <div>
                          <Label htmlFor="notification_email">Notification Email (Receive Bookings)</Label>
                          <Input
                            id="notification_email"
                            type="email"
                            value={emailSettings.notification_email || ''}
                            onChange={(e) => setEmailSettings({...emailSettings, notification_email: e.target.value})}
                            placeholder="jbrantley@jenniferbrantleylaw.com"
                            className="border-[#E8E4DC]"
                            data-testid="notification-email-input"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Save Button */}
                    <div className="pt-4 border-t border-[#E8E4DC]">
                      <Button
                        onClick={saveEmailSettings}
                        disabled={savingEmail}
                        className="bg-[#1F2A44] hover:bg-[#151D30] text-white"
                        data-testid="save-email-settings"
                      >
                        <Save className="w-4 h-4 mr-2" />
                        {savingEmail ? 'Saving...' : 'Save Settings'}
                      </Button>
                    </div>
                  </div>

                  {/* Test Email Section */}
                  <div className="bg-white border border-[#E8E4DC] p-6">
                    <h3 className="font-semibold text-[#222222] mb-4 flex items-center gap-2">
                      <Send className="w-4 h-4" />
                      Test Email Configuration
                    </h3>
                    <p className="text-sm text-[#555555] mb-4">
                      Send a test email to verify your SMTP settings are working correctly.
                    </p>
                    <div className="flex gap-3">
                      <Input
                        type="email"
                        value={testEmail}
                        onChange={(e) => setTestEmail(e.target.value)}
                        placeholder="Enter email to receive test"
                        className="max-w-sm border-[#E8E4DC]"
                        data-testid="test-email-input"
                      />
                      <Button
                        onClick={sendTestEmailHandler}
                        disabled={sendingTest || !emailSettings.smtp_enabled}
                        variant="outline"
                        className="border-[#C2A46D] text-[#C2A46D] hover:bg-[#C2A46D] hover:text-white"
                        data-testid="send-test-email"
                      >
                        <Send className="w-4 h-4 mr-2" />
                        {sendingTest ? 'Sending...' : 'Send Test'}
                      </Button>
                    </div>
                    {!emailSettings.smtp_enabled && (
                      <p className="text-sm text-red-500 mt-2">
                        Enable email notifications and save settings before testing
                      </p>
                    )}
                  </div>

                  {/* Help Section */}
                  <div className="bg-[#F5F3EF] border border-[#E8E4DC] p-6">
                    <h3 className="font-semibold text-[#222222] mb-3">Setup Instructions</h3>
                    <ol className="list-decimal list-inside space-y-2 text-sm text-[#555555]">
                      <li>Log in to your Zoho Mail account</li>
                      <li>If you have Two-Factor Authentication enabled, go to <strong>Settings → Security → App Passwords</strong> and generate a new app password</li>
                      <li>Choose the correct SMTP server based on your account type:
                        <ul className="list-disc list-inside ml-4 mt-1">
                          <li><strong>smtp.zoho.com</strong> - For personal/free Zoho accounts</li>
                          <li><strong>smtppro.zoho.com</strong> - For paid Zoho Workplace accounts</li>
                        </ul>
                      </li>
                      <li>Enter your Zoho email address and password/app password above</li>
                      <li>Save settings and send a test email to verify configuration</li>
                    </ol>
                  </div>
                </div>
              )}
            </>
          )}
        </main>
      </div>
    </div>
  );
};

export default AdminDashboard;
